import { I18n } from "@assets/strings";
import { useObservable } from "micro-observables";
import React, { useState } from "react";
import { useHistory } from "@core/routing/useHistory";
import { ClipLoader } from "react-spinners";
import styled from "styled-components";
import { Path } from "@core/routing/path";
import { useServices } from "@core/servicesContext";
import { ApplicationDraft } from "@domain/application/applicationDraft";
import { useCurrentProfile } from "@domain/currentProfile/hooks";
import { DefaultButton } from "@ui/components/buttons/defaultButton";
import { PrimaryButton } from "@ui/components/buttons/primaryButton";
import { stack } from "@ui/components/layout";
import { theme } from "@ui/styles/theme";
import { desktop, mobile } from "@ui/styles/utils";
import { createToast } from "@ui/toasts/utils";
import { useApplicationEditorController } from "@ui/pages/application/applicationEditorPage/applicationEditorController/applicationEditorControllerContext";

interface ApplicationEditorNavigationProps extends React.HTMLAttributes<HTMLDivElement> {
	applicationId?: string;
}

export const ApplicationEditorNavigation: React.FC<ApplicationEditorNavigationProps> = ({
	applicationId,
	...htmlProps
}) => {
	const controller = useApplicationEditorController();
	const step = useObservable(controller.step);
	const application = useObservable(controller.application);
	const isApplicationValid = useObservable(controller.isApplicationValid);

	const { applicationService, trackingService } = useServices();
	const [isDraftloading, setIsDraftloading] = useState(false);
	const [loading, setLoading] = useState(false);

	const history = useHistory();
	const currentProfile = useCurrentProfile();

	if (!applicationService) {
		return null;
	}

	const onSaveApplication = async (toast?: "toast"): Promise<void> => {
		setIsDraftloading(true);
		try {
			const applicationDraft: Partial<ApplicationDraft> = { ...application };
			delete applicationDraft["levels"];
			delete applicationDraft["degrees"];
			delete applicationDraft["credits"];

			await applicationService.save({
				...applicationDraft,
				countryResidence: application.countryResidence,
			} as ApplicationDraft);
			if (toast) {
				createToast(I18n.t("application.toast.save.title"), I18n.t("application.toast.save.message"), "success");
			}
			if (step === "STUDY_CHOICES") {
				trackingService.track("Step1_StudyChoice", {
					level: applicationDraft.level?.name,
					degree: applicationDraft.degree?.name,
					credit: applicationDraft.credit,
					desiredStartDate: applicationDraft.desiredStartDate,
					userId: currentProfile?.id,
				});
			}
			if (step === "PERSONAL_INFORMATIONS") {
				trackingService.track("Step2_PersonalInfo", {
					firstName: applicationDraft.firstName,
					lastName: applicationDraft.familyName,
					countryResidence: applicationDraft.countryResidence,
					nationality: applicationDraft.nationality,
					userId: currentProfile?.id,
				});
			}
			if (step === "UPLOAD_DOCUMENTS") {
				trackingService.track("Step3_SupportingDocs", {
					identity: applicationDraft.idPassport,
					residency: applicationDraft.proofResidence,
					diploma: applicationDraft.diploma,
					resume: applicationDraft.resume,
					financial_declaration: applicationDraft.selfDeclarationFinancialNeed,
					userId: currentProfile?.id,
				});
			}
			if (step === "VIDEO_STORY") {
				trackingService.track("Step4_VideoStory", {
					story_video: applicationDraft.videoId?.displayName,
					userId: currentProfile?.id,
				});
			}
		} catch {
			createToast(I18n.t("application.toast.save.title"), I18n.t("application.toast.save.error"), "error");
		} finally {
			setIsDraftloading(false);
		}
	};

	const finishApplication = async (): //redirect?: "application" | "editor"
	Promise<void> => {
		setLoading(true);
		try {
			const applicationDraft: Partial<ApplicationDraft> = { ...application };
			delete applicationDraft["levels"];
			delete applicationDraft["degrees"];
			delete applicationDraft["credits"];

			const data = await applicationService.create({
				...applicationDraft,
				countryResidence: application.countryResidence,
			} as ApplicationDraft);
			if (currentProfile) {
				if (data.id) {
					trackingService.track("completed_application");
					history.push({
						pathname: Path.Profile(currentProfile),
						search: `?applicationId=${data.id}`,
						state: { applicationId: data.id },
					});
				}
			}
			//modal &&	createModal((close) => <CongralutationModal close={close} />)
		} catch {
			createToast(I18n.t("application.toast.save.title"), I18n.t("application.toast.save.error"), "error");
		} finally {
			setLoading(false);
		}
	};

	return (
		<Container {...htmlProps} isFinish={step === "SUMMARY"}>
			{step !== "SUMMARY" &&
				(isDraftloading ? (
					<ClipLoader />
				) : (
					<StyledSaveButton onClick={() => onSaveApplication("toast")}>
						{I18n.t("application.save_draft_button")}
					</StyledSaveButton>
				))}
			<NavigationContainer>
				{step !== "STUDY_CHOICES" && (
					<PrimaryButton disabled={isDraftloading} onClick={() => controller.previous()}>
						{I18n.t("back")}
					</PrimaryButton>
				)}
				{step !== "SUMMARY" ? (
					<PrimaryButton disabled={isDraftloading} onClick={() => controller.next()}>
						{I18n.t("next")}
					</PrimaryButton>
				) : loading ? (
					<ClipLoader />
				) : (
					<PrimaryButton onClick={() => finishApplication()} disabled={!isApplicationValid}>
						{I18n.t("application.save_button")}
					</PrimaryButton>
				)}
			</NavigationContainer>
		</Container>
	);
};

const Container = styled.div.withConfig({
	shouldForwardProp: (prop) => prop !== "isFinish",
})<{ isFinish?: boolean }>`
	@media ${desktop} {
		display: flex;
		gap: 12px;
		align-items: center;
		justify-content: ${({ isFinish }) => (isFinish ? "flex-end" : "space-between")};
	}
	@media ${mobile} {
		${stack(8, "flex-end", "center")};
	}
`;

const NavigationContainer = styled.div`
	display: flex;
	gap: 8px;
	align-items: center;
`;

const StyledSaveButton = styled(DefaultButton)`
	background-color: ${theme.colors.white};
	color: ${theme.colors.secondary};
	:hover {
		background-color: ${theme.colors.lightergray};
	}
`;
